import { useMemo } from "react";
import type { BucketOption } from "../utils/types";
import type { Payment } from "../utils/interfaces";

export const useFilteredInvoices = (
  invoices: Payment[] | undefined,
  bucket: BucketOption,
): Payment[] => {
  return useMemo(() => {
    if (!invoices) return [];

    const now = new Date();
    // Set to start of today for accurate date comparisons
    now.setHours(0, 0, 0, 0);
    const millisInDay = 1000 * 60 * 60 * 24;

    const filtered = invoices.filter((invoice) => {
      if (!invoice.due_date) return false;

      const dueDate = new Date(invoice.due_date);
      if (Number.isNaN(dueDate.getTime())) return false;

      // Set to start of day for accurate date comparisons
      dueDate.setHours(0, 0, 0, 0);

      // Only include invoices from today forward
      if (dueDate < now) return false;

      switch (bucket) {
        case "next_7_days": {
          const diffInDays = Math.floor(
            (dueDate.getTime() - now.getTime()) / millisInDay,
          );
          return diffInDays <= 7;
        }
        case "next_30_days": {
          const diffInDays = Math.floor(
            (dueDate.getTime() - now.getTime()) / millisInDay,
          );
          return diffInDays <= 30;
        }
        case "next_year": {
          const nextYear = new Date(now);
          nextYear.setFullYear(now.getFullYear() + 1);
          return dueDate >= now && dueDate < nextYear;
        }
        default:
          return false;
      }
    });

    // Sort by due_date ascending to ensure chronological order
    return filtered.sort(
      (a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
    );
  }, [bucket, invoices]);
};

